import { useTranslations } from 'next-intl';
import type { ScoreStatistics } from '@/lib/api';
import { ScoreComparison } from '../ScoreComparison';

interface ScoreHeaderProps {
    title?: string;
    saving: boolean;
    overallScore: number | null;
    clusterScore: number | null;
    cluster: 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL';
    progress: { scored: number; total: number; percentage: number };
    overallStats?: ScoreStatistics | null;
    clusterStats?: ScoreStatistics | null;
}

export function ScoreHeader({ title, saving, overallScore, clusterScore, cluster, progress, overallStats, clusterStats }: ScoreHeaderProps) {
    const t = useTranslations('certification.stage2');

    return (
        <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4">
            {/* Title on left */}
            <div className="flex items-center gap-3">
                {title && (
                    <h2 className="text-lg font-semibold text-black">
                        {title}
                    </h2>
                )}
                {saving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-black" />
                )}
            </div>
            {/* Scores on right */}
            <div className="flex items-center gap-4">
                {/* Overall Score */}
                <div className="text-center px-3 py-1 bg-emerald-50 border border-emerald-200">
                    <div className={`text-2xl font-bold ${overallScore !== null ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {overallScore !== null ? overallScore.toFixed(1) : '—'}
                    </div>
                    <div className="text-[10px] text-black uppercase tracking-wide font-medium">
                        {t('overall')}
                    </div>
                    {overallStats && (
                        <div className="mt-1">
                            <ScoreComparison userScore={overallScore} stats={overallStats} compact />
                        </div>
                    )}
                </div>
                <div className="h-12 w-px bg-slate-200" />
                {/* Cluster Score */}
                <div className="text-center">
                    <div className={`text-3xl font-bold ${clusterScore !== null ? 'text-black' : 'text-slate-400'}`}>
                        {clusterScore !== null ? clusterScore.toFixed(1) : '—'}
                    </div>
                    <div className="text-xs text-black uppercase tracking-wide">
                        {t('clusterScore', { cluster: t(cluster.toLowerCase() as 'environment' | 'design' | 'economic' | 'social') })}
                    </div>
                    {clusterStats && (
                        <div className="mt-1">
                            <ScoreComparison userScore={clusterScore} stats={clusterStats} compact />
                        </div>
                    )}
                </div>
                <div className="h-12 w-px bg-slate-200" />
                <div>
                    <div className="text-sm text-black mb-1">
                        {t('progressLabel', { scored: progress.scored, total: progress.total, percentage: progress.percentage })}
                    </div>
                    <div className="w-48 bg-slate-200 h-2">
                        <div
                            className="bg-black h-2 transition-all duration-300"
                            style={{ width: `${progress.percentage}%` }}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
}
